import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "flex items-center gap-2 mb-6" }
const _hoisted_2 = {
  key: 0,
  class: "flex items-center justify-center py-12"
}
const _hoisted_3 = {
  key: 1,
  class: "text-center py-12"
}
const _hoisted_4 = { class: "h-16 w-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4" }
const _hoisted_5 = {
  key: 2,
  class: "space-y-4"
}
const _hoisted_6 = { class: "flex items-start justify-between gap-4" }
const _hoisted_7 = { class: "flex items-center gap-3" }
const _hoisted_8 = { class: "flex items-center gap-2 mb-1" }
const _hoisted_9 = { class: "text-xs text-muted-foreground whitespace-nowrap" }
const _hoisted_10 = { class: "text-sm text-muted-foreground whitespace-pre-line" }

import { ref, onMounted } from 'vue'
import { collection, query, orderBy, limit, getDocs, where, Timestamp } from '@/lib/firestore'
import { db } from '@/config/firebase'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Newspaper, Megaphone, Sparkles, AlertTriangle, Info, Loader2 } from 'lucide-vue-next'

interface NewsItem {
  id: string
  title: string
  content: string
  type: 'announcement' | 'feature' | 'maintenance' | 'tip'
  publishedAt: Date
  expiresAt?: Date
  pinned?: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'NewsSection',
  setup(__props) {

const news = ref<NewsItem[]>([])
const isLoading = ref(true)

const typeConfig = {
  announcement: {
    label: 'Anuncio',
    icon: Megaphone,
    color: 'bg-blue-500/10 text-blue-600',
    badgeVariant: 'default' as const,
  },
  feature: {
    label: 'Nueva Funcionalidad',
    icon: Sparkles,
    color: 'bg-green-500/10 text-green-600',
    badgeVariant: 'secondary' as const,
  },
  maintenance: {
    label: 'Mantenimiento',
    icon: AlertTriangle,
    color: 'bg-yellow-500/10 text-yellow-600',
    badgeVariant: 'outline' as const,
  },
  tip: {
    label: 'Tip',
    icon: Info,
    color: 'bg-purple-500/10 text-purple-600',
    badgeVariant: 'secondary' as const,
  },
}

const formatDate = (date: Date): string => {
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))

  if (days === 0) return 'Hoy'
  if (days === 1) return 'Ayer'
  if (days < 7) return `Hace ${days} días`

  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: 'short',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  }).format(date)
}

onMounted(async () => {
  try {
    // Cargar noticias de la colección 'news'
    // Solo mostrar las que no han expirado y ordenadas por fecha
    const now = Timestamp.now()
    const newsQuery = query(
      collection(db, 'news'),
      orderBy('publishedAt', 'desc'),
      limit(20)
    )
    const snapshot = await getDocs(newsQuery)

    const items: NewsItem[] = []
    snapshot.forEach((doc) => {
      const data = doc.data()
      const expiresAt = data.expiresAt?.toDate?.()

      // Filtrar expiradas en cliente (Firestore no soporta bien comparaciones de fecha con orderBy)
      if (!expiresAt || expiresAt > new Date()) {
        items.push({
          id: doc.id,
          title: data.title,
          content: data.content,
          type: data.type || 'announcement',
          publishedAt: data.publishedAt?.toDate?.() || new Date(),
          expiresAt,
          pinned: data.pinned || false,
        })
      }
    })

    // Ordenar: pinned primero, luego por fecha
    news.value = items.sort((a, b) => {
      if (a.pinned && !b.pinned) return -1
      if (!a.pinned && b.pinned) return 1
      return b.publishedAt.getTime() - a.publishedAt.getTime()
    })
  } catch (error) {
    console.error('Error loading news:', error)
  } finally {
    isLoading.value = false
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_unref(Newspaper), { class: "h-6 w-6 text-primary" }),
      _cache[0] || (_cache[0] = _createElementVNode("h2", { class: "text-2xl font-bold" }, "Noticias y Anuncios", -1))
    ]),
    _cache[4] || (_cache[4] = _createElementVNode("p", { class: "text-muted-foreground mb-6" }, " Mantente al día con las últimas novedades, actualizaciones y consejos de la plataforma. ", -1)),
    (isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(_unref(Loader2), { class: "h-8 w-8 animate-spin text-muted-foreground" })
        ]))
      : (news.value.length === 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_unref(Newspaper), { class: "h-8 w-8 text-muted-foreground" })
            ]),
            _cache[1] || (_cache[1] = _createElementVNode("h3", { class: "text-lg font-semibold mb-2" }, "No hay noticias", -1)),
            _cache[2] || (_cache[2] = _createElementVNode("p", { class: "text-muted-foreground" }, " Las nuevas actualizaciones y anuncios aparecerán aquí. ", -1))
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_5, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(news.value, (item) => {
              return (_openBlock(), _createBlock(_unref(Card), {
                key: item.id,
                class: _normalizeClass(item.pinned ? 'border-primary/50 bg-primary/5' : '')
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(CardHeader), { class: "pb-3" }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_6, [
                        _createElementVNode("div", _hoisted_7, [
                          _createElementVNode("div", {
                            class: _normalizeClass(["h-10 w-10 rounded-lg flex items-center justify-center", typeConfig[item.type].color])
                          }, [
                            (_openBlock(), _createBlock(_resolveDynamicComponent(typeConfig[item.type].icon), { class: "h-5 w-5" }))
                          ], 2),
                          _createElementVNode("div", null, [
                            _createElementVNode("div", _hoisted_8, [
                              _createVNode(_unref(Badge), {
                                variant: typeConfig[item.type].badgeVariant,
                                class: "text-xs"
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(typeConfig[item.type].label), 1)
                                ]),
                                _: 2
                              }, 1032, ["variant"]),
                              (item.pinned)
                                ? (_openBlock(), _createBlock(_unref(Badge), {
                                    key: 0,
                                    variant: "outline",
                                    class: "text-xs"
                                  }, {
                                    default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                                      _createTextVNode(" Fijado ", -1)
                                    ]))]),
                                    _: 1
                                  }))
                                : _createCommentVNode("", true)
                            ]),
                            _createVNode(_unref(CardTitle), { class: "text-base" }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(item.title), 1)
                              ]),
                              _: 2
                            }, 1024)
                          ])
                        ]),
                        _createElementVNode("span", _hoisted_9, _toDisplayString(formatDate(item.publishedAt)), 1)
                      ])
                    ]),
                    _: 2
                  }, 1024),
                  _createVNode(_unref(CardContent), null, {
                    default: _withCtx(() => [
                      _createElementVNode("p", _hoisted_10, _toDisplayString(item.content), 1)
                    ]),
                    _: 2
                  }, 1024)
                ]),
                _: 2
              }, 1032, ["class"]))
            }), 128))
          ]))
  ]))
}
}

})